import { useContext, useEffect } from 'react';
import { buildComponentDocsPath, buildStringFromSegments, logToConsole } from '../util';
import { HawkinsContext } from './useHawkinsContext';
/**
 * A hook that conditionally logs a message to the browser console.
 *
 * @private
 */
export function useConsoleLog(condition, message, _a) {
    var _b = _a === void 0 ? {} : _a, disabled = _b.disabled, displayName = _b.displayName, docsPath = _b.docsPath, _c = _b.level, level = _c === void 0 ? 'warn' : _c, _d = _b.printInProd, printInProd = _d === void 0 ? false : _d;
    useEffect(function () {
        if (condition)
            logToConsole(message, { disabled: disabled, displayName: displayName, docsPath: docsPath, level: level, printInProd: printInProd });
    }, [condition, disabled, displayName, docsPath, level, message, printInProd]);
}
/**
 * A hook that can log a warning for a deprecated CSS class.
 *
 * @private
 */
export function useClassDeprecationWarning(deprecatedName, classes, _a) {
    var _b = _a === void 0 ? {} : _a, displayName = _b.displayName, _c = _b.docsPath, docsPath = _c === void 0 ? true : _c, replacementInstructions = _b.replacementInstructions, replacementName = _b.replacementName;
    var hideDeprecationWarnings = useContext(HawkinsContext).hideDeprecationWarnings;
    useConsoleLog((classes === null || classes === void 0 ? void 0 : classes[deprecatedName]) !== undefined, buildStringFromSegments([
        "`classes.".concat(String(deprecatedName), "` is deprecated."),
        replacementName && "Please use `classes.".concat(String(replacementName), "` instead."),
        replacementInstructions,
        'See http://go.netflix.com/hawkins-professional-v4-deprecations for codemod assistance.',
    ]), {
        displayName: displayName,
        docsPath: 
        // TODO(improvement): Change `buildComponentDocsPath` to `buildDocsPath` and detect hook versus component.
        docsPath === true ? (displayName ? buildComponentDocsPath(displayName, 'styles') : undefined) : docsPath,
        level: 'warn',
        disabled: hideDeprecationWarnings,
    });
}
/**
 * A hook that can log a warning for multiple deprecated CSS classes.
 *
 * @private
 */
export function useClassDeprecationWarnings(deprecationData, classes, _a) {
    var _b = _a === void 0 ? {} : _a, displayName = _b.displayName, docsPath = _b.docsPath;
    deprecationData.forEach(function (_a) {
        var name = _a.name, replacementInstructions = _a.replacementInstructions, replacementName = _a.replacementName;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useClassDeprecationWarning(name, classes, { displayName: displayName, docsPath: docsPath, replacementInstructions: replacementInstructions, replacementName: replacementName });
    });
}
/**
 * A hook that can log a warning for a deprecated component prop.
 *
 * @private
 */
export function usePropDeprecationWarning(deprecatedName, props, _a) {
    var _b = _a === void 0 ? {} : _a, displayName = _b.displayName, _c = _b.docsPath, docsPath = _c === void 0 ? true : _c, nestedSourceName = _b.nestedSourceName, replacementInstructions = _b.replacementInstructions, replacementName = _b.replacementName;
    var hideDeprecationWarnings = useContext(HawkinsContext).hideDeprecationWarnings;
    useConsoleLog(props[deprecatedName] !== undefined, buildStringFromSegments([
        "`props.".concat(nestedSourceName ? "".concat(nestedSourceName, ".") : '').concat(String(deprecatedName), "` is deprecated."),
        replacementName && "Please use `props.".concat(String(replacementName), "` instead."),
        replacementInstructions,
        'See http://go.netflix.com/hawkins-professional-v4-deprecations for codemod assistance.',
    ]), {
        displayName: displayName,
        // TODO(improvement): Change `buildComponentDocsPath` to `buildDocsPath` and detect hook versus component.
        docsPath: docsPath === true ? (displayName ? buildComponentDocsPath(displayName, 'props') : undefined) : docsPath,
        level: 'warn',
        disabled: hideDeprecationWarnings,
    });
}
/**
 * A hook that can log a warning for multiple deprecated component props.
 *
 * @private
 */
export function usePropDeprecationWarnings(deprecationData, props, _a) {
    var _b = _a === void 0 ? {} : _a, displayName = _b.displayName, docsPath = _b.docsPath, nestedSourceName = _b.nestedSourceName;
    deprecationData.forEach(function (_a) {
        var name = _a.name, replacementInstructions = _a.replacementInstructions, replacementName = _a.replacementName;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        usePropDeprecationWarning(name, props, {
            displayName: displayName,
            docsPath: docsPath,
            nestedSourceName: nestedSourceName,
            replacementInstructions: replacementInstructions,
            replacementName: replacementName,
        });
    });
}
